import React, { useEffect, useState } from "react";
import api from '../utils/api';
import Loader from "../lib/components/Loader";
import constants from "../lib/constants";

const FranchiseTags = () => {
    const [tagCandidates, setTagCandidates] = useState({}); // Record<manager, PlayerRow[]>
    const [savedTags, setSavedTags] = useState([]);         // PlayerRow[] with franchisetag IS NOT NULL
    const [pendingTags, setPendingTags] = useState({});     // Record<manager, playerId string | "">
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showCutModal, setShowCutModal] = useState(false);
    const [cutting, setCutting] = useState(false);
    const [cutError, setCutError] = useState(null);
    const [cutResult, setCutResult] = useState(null); // { cutCount }

    const fetchData = () => {
        const candidatesRequest = api.get('api/v1/players/offseason/tagCandidates');
        const savedTagsRequest = api.get('api/v1/players/franchiseTags');

        return Promise.all([candidatesRequest, savedTagsRequest])
            .then(([candidatesRes, savedTagsRes]) => {
                const candidates = candidatesRes.data || [];
                const saved = savedTagsRes.data || [];

                const grouped = candidates.reduce((acc, player) => {
                    if (!acc[player.manager]) acc[player.manager] = [];
                    acc[player.manager].push(player);
                    return acc;
                }, {});
                setTagCandidates(grouped);
                setSavedTags(saved);

                // Pre-populate only managers who already have a franchise tag saved in the DB.
                // Managers without a saved tag start at "— No tag —" and must be explicitly selected.
                const initial = {};
                constants.managerList.forEach(manager => {
                    const alreadyTagged = saved.find(p => p.franchisetag === manager);
                    initial[manager] = alreadyTagged ? String(alreadyTagged.id) : "";
                });
                setPendingTags(initial);
            });
    };

    useEffect(() => {
        fetchData()
            .catch(err => console.error('Failed to load franchise tag data', err))
            .finally(() => setLoading(false));
    }, []);

    const handleSelectionChange = (manager, playerId) => {
        setSaveSuccess(false);
        setSaveError(null);
        setPendingTags(prev => ({ ...prev, [manager]: playerId }));
    };

    const handleSave = () => {
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        const tags = constants.managerList
            .filter(manager => pendingTags[manager] != null && pendingTags[manager] !== "")
            .map(manager => ({
                manager,
                playerId: Number(pendingTags[manager]),
            }));

        api.post('api/v1/players/franchiseTags', { tags })
            .then(({ data }) => {
                setSavedTags(data);
                setSaveSuccess(true);
                return api.get('api/v1/players/offseason/tagCandidates');
            })
            .then(({ data: candidates }) => {
                const grouped = (candidates || []).reduce((acc, player) => {
                    if (!acc[player.manager]) acc[player.manager] = [];
                    acc[player.manager].push(player);
                    return acc;
                }, {});
                setTagCandidates(grouped);
            })
            .catch(err => {
                const message = err?.response?.data?.message || 'Failed to save franchise tags';
                setSaveError(message);
            })
            .finally(() => setSaving(false));
    };

    const handleCutConfirm = () => {
        setCutting(true);
        setCutError(null);

        api.post('api/v1/players/offseason/cut')
            .then(({ data }) => {
                setCutResult(data);
                setShowCutModal(false);
                // Re-fetch everything — cut players are now free agents
                return fetchData();
            })
            .catch(err => {
                const message = err?.response?.data?.message || 'Failed to cut players';
                setCutError(message);
            })
            .finally(() => setCutting(false));
    };

    const canCut = savedTags.length > 0;

    return (
        <div>
            {loading ? <Loader /> : (
                <>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Manager</th>
                                <th>Franchise Tag</th>
                            </tr>
                        </thead>
                        <tbody>
                            {constants.managerList.map(manager => {
                                const eligible = tagCandidates[manager] || [];
                                const hasEligible = eligible.length > 0;

                                return (
                                    <tr key={manager}>
                                        <td>{manager}</td>
                                        <td>
                                            {hasEligible ? (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={pendingTags[manager] || ""}
                                                    onChange={e => handleSelectionChange(manager, e.target.value)}
                                                >
                                                    <option value="">— No tag —</option>
                                                    {eligible.map(player => (
                                                        <option key={player.id} value={String(player.id)}>
                                                            {player.name} ({player.posname})
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="text-muted">No eligible players</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {saveError && (
                        <div className="alert alert-danger mt-2" role="alert">
                            {saveError}
                        </div>
                    )}
                    {saveSuccess && (
                        <div className="alert alert-success mt-2" role="alert">
                            Franchise tags saved.
                        </div>
                    )}
                    {cutResult && (
                        <div className="alert alert-success mt-2" role="alert">
                            Done — {cutResult.cutCount} player{cutResult.cutCount !== 1 ? 's' : ''} released.
                        </div>
                    )}

                    <div className="d-flex gap-2 mt-3">
                        <button
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Save Franchise Tags'}
                        </button>
                        <button
                            className="btn btn-danger"
                            onClick={() => { setCutError(null); setShowCutModal(true); }}
                            disabled={!canCut}
                            title={!canCut ? "Save franchise tags first" : ""}
                        >
                            Cut 0-Year Players
                        </button>
                    </div>

                    {/* Warning modal */}
                    {showCutModal && (
                        <>
                            <div
                                className="modal-backdrop fade show"
                                onClick={() => !cutting && setShowCutModal(false)}
                            />
                            <div
                                className="modal fade show"
                                style={{ display: 'block' }}
                                role="dialog"
                                aria-modal="true"
                            >
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Cut 0-Year Players</h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => setShowCutModal(false)}
                                                disabled={cutting}
                                                aria-label="Close"
                                            />
                                        </div>
                                        <div className="modal-body">
                                            <p>
                                                This will permanently remove all players with <strong>0 contract years</strong> from
                                                their rosters. Franchise-tagged players are safe. This cannot be undone.
                                            </p>
                                            <p>Are you sure?</p>
                                            {cutError && (
                                                <div className="alert alert-danger" role="alert">
                                                    {cutError}
                                                </div>
                                            )}
                                        </div>
                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={() => setShowCutModal(false)}
                                                disabled={cutting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={handleCutConfirm}
                                                disabled={cutting}
                                            >
                                                {cutting ? 'Cutting…' : 'Yes, cut players'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default FranchiseTags;
